import React, {useRef, useEffect} from 'react';
import Highcharts, {Chart as HighchartsChart} from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {Typography} from '@mui/material';
import {
  ProcessingSummaryReportType,
  ProcessingSummaryTypeMapToCaption,
} from '../ProcessingSummaryTab';
import {t} from 'i18next';

interface LineChartProps {
  data: ProcessingSummaryReportType;
}

const Y_AXIS_INTERVAL_COUNT = 4;

const LineChart: React.FC<LineChartProps> = ({data}) => {
  const chartRef = useRef<HighchartsReact.RefObject | null>(null);

  useEffect(() => {
    const chartInstance = chartRef.current?.chart as
      | HighchartsChart
      | undefined;

    if (chartInstance) {
      const xAxis = chartInstance.xAxis[0];

      // Clear existing custom plot lines
      if ((chartInstance as any).customPlotLines) {
        (chartInstance as any).customPlotLines.forEach((line: any) =>
          line.destroy()
        );
      }
      (chartInstance as any).customPlotLines = [];
      const offsetY = 0;
      // Add new custom plot lines based on x-axis categories
      xAxis.categories?.forEach((_, index) => {
        const xPosition = xAxis.toPixels(index, false);

        const line = (chartInstance as any).renderer
          .path([
            'M',
            xPosition,
            chartInstance.plotTop + offsetY,
            'L',
            xPosition,
            chartInstance.plotTop + offsetY + 210,
          ])
          .attr({
            stroke: '#4C6181',
            'stroke-width': 0.84,
            zIndex: 1,
          })
          .add();
        if (!(chartInstance as any).customPlotLines) {
          (chartInstance as any).customPlotLines = [];
        }
        (chartInstance as any).customPlotLines.push(line);
      });
    }
  }, [data]); // Re-run effect if `data` changes

  const maxFileCount = Math.max(
    ...data.processingSummary.map((processingSummary) => processingSummary.fileCount),
    0
  );
  
  let tickInterval = Math.ceil(maxFileCount / Y_AXIS_INTERVAL_COUNT) || 1;
  
  if (tickInterval >= 1000) {
    tickInterval = Math.ceil(tickInterval / 1000) * 1000;
  }
  const options: Highcharts.Options = {
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
      spacingTop: 16,
      height: 357,
      spacingBottom: 90,
    },
    title: {
      text: '',
    },
    xAxis: {
      categories: data.processingSummary.map((processingSummary) =>
        t(
          `reports.processingSummary.${ProcessingSummaryTypeMapToCaption[processingSummary.type]}`
        )
      ),
      lineWidth: 0,
      labels: {
        style: {
          color: '#C9D1DC',
          fontSize: '14px',
          fontWeight: '400',
          fontFamily: 'Inter',
          textAlign: 'Center',
        },
        align: 'center',
        padding: 19,
        formatter: function () {
          const labelText = this.value.toString();
          const words = labelText.split(' ');
          return words.length > 1
            ? words.slice(0, 2).join(' ') + '<br/>' + words.slice(2).join(' ')
            : labelText;
        },
        useHTML: true,
      },
      min: 0,
      tickInterval: 1,
    },
    yAxis: {
      title: {
        text: null,
      },
      tickInterval: tickInterval,
      gridLineWidth: 0,
      offset: 0,
      top: 25,
      labels: {
        x: 65,
        align: 'right',
        style: {
          color: '#C9D1DC',
          width: 20,
          fontSize: '14px',
          fontWeight: '400',
          fontFamily: 'Inter',
        },
        useHTML: true,
      },
    },
    series: [
      {
        name: 'Files Processed',
        data: data.processingSummary.map(
          (processingSummary) => processingSummary.fileCount
        ),
        color: '#6E87FF',
        type: 'line',
        marker: {
          radius: 8,
          fillColor: '#6E87FF',
        },
      },
    ],
    plotOptions: {
      line: {
        dataLabels: {
          enabled: false,
        },
        enableMouseTracking: false,
      },
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
  };

  return (
    <div className="line-chart">
      <div className="chart" data-testid="line-chart-container">
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartRef}
        />
      </div>
      <Typography className="typo-text body4_size line-chart-desc">
        {t('reports.processingHistoryChart.lineChart.description')}
      </Typography>
    </div>
  );
};

export default LineChart;
